import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';
import SideModal from '../../components/SideModal';
import Notification from '../../components/Notification';
import Form from '../../components/Form';
import Button from '../../components/Button';
import Icon from '../../components/Icon';
import TextArea from '../../components/TextArea';
import {T} from '../../components/I18n';
import CanReviewTemplateData from '../../security/rules/CanReviewTemplateData';
import CanSecondLevelReviewTemplateData from '../../security/rules/CanSecondLevelReviewTemplateData';

// Confirms and then applies one review state to every response awaiting the current user's review. The responses are
// taken from the ones the screen has already loaded, so this only ever reviews what the table is showing. They are sent
// to the bulk review endpoint in batches so a large selection never goes out as one oversized request.
const BATCH_SIZE = 100;

class BulkReviewModal extends PureComponent {
  state = {
    comment: '',
    submitting: false,
    progress: 0
  };

  componentWillUnmount() {
    this.unmounted = true;
  }

  // A response is only reviewable when it is waiting on a review this user is allowed to cast, and nobody can cast a
  // second-level review on a response they already reviewed at the first level.
  reviewableRows() {
    const {user, template, rows} = this.props;
    const canReview = CanReviewTemplateData(user, template);
    const canSecondLevelReview = template.secondLevelReviewEnabled && CanSecondLevelReviewTemplateData(user, template);

    return Object.keys(rows).reduce((reviewable, sheetId) => reviewable.concat(rows[sheetId].filter(row => {
      if (row.reviewState === 'PENDING') {
        return canReview;
      }

      if (row.reviewState === 'IN_REVIEW') {
        return canSecondLevelReview && (!row.reviewer || row.reviewer.id !== user.id);
      }

      return false;
    })), []);
  }

  handleSubmit = async () => {
    const {credentials, reviewState, onComplete, template} = this.props;
    const {comment} = this.state;
    const action = reviewState === 'APPROVED' ? 'approved' : 'rejected';
    const rows = this.reviewableRows();
    let reviewed = 0;
    let failures = 0;

    this.setState({submitting: true, progress: 0});

    // Sent in serial batches to keep any single bulk review request small enough for the API to handle.
    const rowIds = rows.map(row => row.id);
    const templateId = template.id;

    for (let i = 0; i < rowIds.length; i += BATCH_SIZE) {
      const batch = rowIds.slice(i, i + BATCH_SIZE);

      try {
        await api.bulkReviewSheetRows(templateId, batch, reviewState, comment, credentials);
        reviewed += batch.length;
      }
      catch (e) {
        failures += batch.length;
      }

      if (this.unmounted) {
        return;
      }

      this.setState({progress: reviewed + failures});
    }

    if (failures < 1) {
      NotificationManager.success(`${reviewed} response(s) were ${action}.`);
    }
    else if (reviewed > 0) {
      NotificationManager.warning(`${reviewed} response(s) were ${action}, but ${failures} could not be reviewed. Please try again.`);
    }
    else {
      NotificationManager.error('None of the responses could be reviewed. Please try again.');
    }

    this.setState({submitting: false, comment: ''});
    onComplete(reviewed);
  }

  handleClose = () => {
    this.setState({comment: ''});
    this.props.onClose();
  }

  renderContent(count) {
    const {template} = this.props;
    const {comment, submitting, progress} = this.state;

    if (submitting) {
      const batches = Math.ceil(count / BATCH_SIZE);

      return (
        <Notification info>
          <T isolate>Reviewed <T ignore>{progress}</T> of <T ignore>{count}</T> response(s)...</T>
          {batches > 1
            ? <ProgressNote>
              <T isolate>
                These are being sent in <T ignore>{batches}</T> batches, one after the other, so this can take a little
                while. Please keep this window open and stay on this page until it finishes — leaving now stops the
                remaining batches, and the responses already reviewed stay reviewed.
              </T>
            </ProgressNote>
            : <ProgressNote>Please keep this window open until every response has been reviewed.</ProgressNote>}
        </Notification>
      );
    }

    if (count < 1) {
      return <Notification info>There are no responses awaiting your review in the data you are currently viewing.</Notification>;
    }

    return (
      <>
        <Form.Group>
          <Notification info>
            <T isolate>
              <T ignore>{count}</T> response(s) of <strong><T noTranslate>{template.name}</T></strong> are awaiting your
              review based on filters applied.
            </T>
          </Notification>
          {/* Only worth mentioning when the selection actually spans more than one request. */}
          {count > BATCH_SIZE &&
            <BatchNote>
              <T isolate>
                These are sent in <T ignore>{Math.ceil(count / BATCH_SIZE)}</T> batches of up
                to <T ignore>{BATCH_SIZE}</T>, one after the other, so this can take a little while. Keep this window
                open and stay on this page until it finishes.
              </T>
            </BatchNote>}
        </Form.Group>
        <Form.Group>
          <TextArea
            id="bulk_review_comment"
            label="Review comment (optional)"
            labelPosition="top"
            placeholder="This comment is added to every response"
            value={comment}
            onChange={e => this.setState({comment: e.target.value})}
          />
        </Form.Group>
      </>
    );
  }

  render() {
    const {reviewState} = this.props;
    const {submitting} = this.state;
    const approving = reviewState === 'APPROVED';
    const count = reviewState ? this.reviewableRows().length : 0;

    return (
      <SideModal
        title={approving ? 'Approve All Data' : 'Reject All Data'}
        open={!!reviewState}
        onClose={submitting ? () => null : this.handleClose}
        options={count > 0 && [
          <Button destructive={!approving} disabled={submitting} onClick={this.handleSubmit}>
            <Icon name={approving ? 'check' : 'slash'} /> {approving ? 'Approve All' : 'Reject All'}
          </Button>,
          <Button secondary disabled={submitting} onClick={this.handleClose}>Cancel</Button>
        ]}>
        <SideModal.Content>
          {/* Nothing is shown behind the overlay while the modal is closed. */}
          {!!reviewState && this.renderContent(count)}
        </SideModal.Content>
      </SideModal>
    );
  }
}



const BatchNote = styled.p`
  margin: 0.5rem 0 0 0;
  color: #747474;
  font-size: 0.875rem;
`;

const ProgressNote = styled.p`
  margin: 0.5rem 0 0 0;
  color: #747474;
  font-size: 0.875rem;
`;

export default withUser(BulkReviewModal);
